import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import {
  enfileirarImpressao, itensVendidos, margens, reposicao, resumoCadastro,
  resumoDoDia, vendasCoringa, vendasDoPeriodo,
} from '../lib/consultas';
import { baixarCsv, paraCsv } from '../lib/csv';
import { brl, dataBR, hojeISO, qtd } from '../lib/formato';
import { diaPorExtenso, imprimirDia, montarDocumentoDoDia } from '../lib/diaDoc';
import { mensagemDeErro } from '../lib/supabase';
import { podeGerenciarCatalogo, podeVerFaturamento, useSessao } from '../lib/sessao';

// Tela 8 — relatórios. Cada bloco responde uma pergunta que hoje não tem
// resposta na loja: quanto vendi, quando vendo, o que mais sai, o que vai
// faltar, onde a margem está apertada e quanto do estoque já está no sistema.
//
// DUAS TELAS NUMA, desde 28/09/2026. O dono mandou tirar faturamento da equipe
// ("funcionário não pode ver faturamento, nem quanto vende"), e aqui isso não
// podia ser feito fechando a rota: é nesta tela que mora a LISTA DE REPOSIÇÃO,
// que é trabalho do gerente. Então a régua é por BLOCO:
//
//   gerente   a reposição (em unidades), a qualidade do cadastro e as MARGENS
//             — margem é custo, e custo para o gerente não mudou nesta
//             decisão (a 090 diz isso com todas as letras). Nada de R$ de
//             venda.
//   dono      tudo — a folha do dia, os cartões, os gráficos, as peças mais
//             vendidas e o coringa a auditar.
//
// E as consultas dos blocos escondidos NÃO SAEM DO APARELHO (`enabled`): o
// banco nega as mesmas coisas, e pedido negado viraria faixa vermelha de erro
// numa tela que, para o gerente, está funcionando perfeitamente.

const PERIODOS = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
];

export default function Relatorios() {
  const { papel } = useSessao();
  // Quem vê dinheiro de venda nesta tela. O gerente entra por causa da
  // reposição; o R$ é do dono.
  const verFaturamento = podeVerFaturamento(papel);
  const [dias, setDias] = useState(30);

  const periodo = useMemo(() => {
    const fim = new Date();
    fim.setHours(23, 59, 59, 999);
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - (dias - 1));
    inicio.setHours(0, 0, 0, 0);
    return { inicio: inicio.toISOString(), fim: fim.toISOString() };
  }, [dias]);

  const vendas = useQuery({
    queryKey: ['relatorio', 'vendas', periodo.inicio],
    queryFn: () => vendasDoPeriodo(periodo.inicio, periodo.fim),
    enabled: verFaturamento,
  });

  const itens = useQuery({
    queryKey: ['relatorio', 'itens', periodo.inicio],
    queryFn: () => itensVendidos(periodo.inicio, periodo.fim),
    enabled: verFaturamento,
  });

  // A reposição e a qualidade do cadastro são de estoque: ficam para os dois.
  const falta = useQuery({ queryKey: ['reposicao'], queryFn: reposicao });
  const cadastro = useQuery({ queryKey: ['resumo-cadastro'], queryFn: resumoCadastro });
  // O coringa lista o VALOR de cada venda de peça diversa: só dono.
  const coringa = useQuery({
    queryKey: ['vendas-coringa'], queryFn: vendasCoringa, enabled: verFaturamento,
  });
  // A margem é custo, e custo para o gerente não mudou em 28/09/2026 (a RPC
  // `margens` é definer com exige_papel(['gerente','dono']) desde sempre).
  // A régua é a da rota: quem compra e põe preço.
  const margem = useQuery({
    queryKey: ['margens'], queryFn: () => margens(100), enabled: podeGerenciarCatalogo(papel),
  });

  // VENDA CANCELADA NÃO ENTRA em nada daqui — nem no total, nem no ticket,
  // nem nos gráficos, nem nas peças mais vendidas. Quem a descarta é
  // vendasDoPeriodo/itensVendidos, no servidor; esta tela somava as
  // canceladas até 11/09/2026 e por isso divergia da tela de Vendas.
  const resumo = useMemo(() => {
    const linhas = vendas.data ?? [];
    const total = linhas.reduce((s, v) => s + Number(v.total), 0);
    return {
      total,
      quantidade: linhas.length,
      ticket: linhas.length ? total / linhas.length : 0,
      porDia: agrupar(linhas, (v) => hojeISO(new Date(v.vendida_em)), (v) => Number(v.total)),
      porHora: agrupar(linhas, (v) => String(new Date(v.vendida_em).getHours()), (v) => Number(v.total)),
      porForma: agrupar(linhas, (v) => v.forma_pagamento ?? 'não informado', (v) => Number(v.total)),
    };
  }, [vendas.data]);

  const topPecas = useMemo(() => {
    const mapa = new Map<string, { descricao: string; sku: string; quantidade: number; total: number }>();
    for (const i of itens.data ?? []) {
      const atual = mapa.get(i.product_id) ?? {
        descricao: i.products?.descricao ?? '(peça removida)',
        sku: i.products?.sku ?? '—',
        quantidade: 0,
        total: 0,
      };
      atual.quantidade += Number(i.quantidade);
      atual.total += Number(i.subtotal);
      mapa.set(i.product_id, atual);
    }
    return [...mapa.entries()]
      .sort((a, b) => b[1].quantidade - a[1].quantidade)
      .slice(0, 15);
  }, [itens.data]);

  const erro = vendas.error ?? itens.error ?? falta.error ?? margem.error ?? cadastro.error;

  return (
    <Layout titulo="Relatórios">
      <div className="space-y-4">
        {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}

        {/* O DINHEIRO DA VENDA — só o dono (28/09/2026). A folha do dia, os
            três cartões, os dois gráficos e as peças mais vendidas contam a
            mesma coisa de quatro jeitos: quanto a loja vendeu. O seletor de
            período vem junto porque só alimenta estes blocos. */}
        {verFaturamento && (
          <>
            <FolhaDoDia />

            <div className="flex gap-2">
              {PERIODOS.map((p) => (
                <button
                  key={p.dias}
                  type="button"
                  onClick={() => setDias(p.dias)}
                  aria-pressed={dias === p.dias}
                  className={[
                    'flex-1 border px-4 py-2.5 text-sm font-bold',
                    dias === p.dias ? 'border-mute bg-edge text-paper' : 'border-edge text-mute',
                  ].join(' ')}
                >
                  {p.rotulo}
                </button>
              ))}
            </div>

            {/* Duas colunas no celular: em 375px o cartão de 1/3 corta
                "R$ 1.234,56" (medido). Defeito anterior às moléculas,
                corrigido junto. */}
            <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
              <div className="cartao p-3 sm:p-4">
                <p className="rotulo">Vendido</p>
                <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">{brl(resumo.total)}</p>
              </div>
              <div className="cartao p-3 sm:p-4">
                <p className="rotulo">Vendas</p>
                <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">{resumo.quantidade}</p>
              </div>
              <div className="cartao col-span-2 p-3 sm:col-span-1 sm:p-4">
                <p className="rotulo">Ticket médio</p>
                <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">{brl(resumo.ticket)}</p>
              </div>
            </section>

            <Barras titulo="Vendas por dia" itens={resumo.porDia.slice(-30)} formatoRotulo={dataBR} />
            <Barras
              titulo="Vendas por hora do dia"
              itens={[...resumo.porHora].sort((a, b) => Number(a[0]) - Number(b[0]))}
              formatoRotulo={(h) => `${String(h).padStart(2, '0')}h`}
            />

            <section className="cartao p-4">
              <h2 className="rotulo">Peças mais vendidas</h2>
              {topPecas.length > 0 ? (
                <ul className="mt-1 divide-y divide-edge">
                  {topPecas.map(([id, p]) => (
                    <li key={id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <Link to={`/peca/${id}`} className="min-w-0 hover:text-signal">
                        <span className="block truncate font-semibold">{p.descricao}</span>
                        <span className="font-mono text-xs text-dim">{p.sku}</span>
                      </Link>
                      <span className="shrink-0 text-right">
                        <span className="block font-mono font-bold">{qtd(p.quantidade)} un</span>
                        <span className="block font-mono text-xs text-mute">{brl(p.total)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-dim">Nenhuma venda registrada no período.</p>
              )}
            </section>
          </>
        )}

        <section className="cartao p-4">
          <div className="flex items-center justify-between">
            <h2 className="rotulo">Abaixo do ponto de pedido</h2>
            <button
              type="button"
              className="botao-terciario"
              disabled={!falta.data?.length}
              onClick={() => baixarCsv('reposicao.csv', paraCsv(
                ['SKU', 'Descrição', 'Categoria', 'Em estoque', 'Pendente de registro', 'Ponto de pedido', 'Comprar'],
                (falta.data ?? []).map((r) => [
                  r.sku, r.descricao, r.categoria ?? '', r.em_estoque, r.pendente_registro,
                  r.reorder_point, r.sugerir_comprar,
                ]),
              ))}
            >
              Exportar CSV
            </button>
          </div>

          {falta.data && falta.data.length > 0 ? (
            <ul className="mt-1 divide-y divide-edge">
              {falta.data.slice(0, 40).map((r) => (
                <li key={r.product_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <Link to={`/peca/${r.product_id}`} className="min-w-0 hover:text-signal">
                    <span className="block truncate font-semibold">{r.descricao}</span>
                    <span className="font-mono text-xs text-dim">{r.sku}</span>
                  </Link>
                  <span className="shrink-0 text-right">
                    <span className="block font-mono text-alerta">{qtd(r.em_estoque)} em estoque</span>
                    {r.pendente_registro > 0 && (
                      <span className="block font-mono text-xs text-alerta">
                        {qtd(r.pendente_registro)} vendida(s) sem registro
                      </span>
                    )}
                    <span className="block font-mono text-xs text-mute">comprar {qtd(r.sugerir_comprar)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-dim">Nada abaixo do ponto de pedido.</p>
          )}
        </section>

        {/* O CORINGA é de dono: a lista traz o VALOR de cada venda de peça
            diversa. A auditoria em si era da gerência, e voltará a ser se o
            dono pedir — hoje ela não existe sem o valor ao lado. */}
        {verFaturamento && (
          <>
            {/* A auditoria diária do coringa (migração 016): o que saiu como
                PEÇA DIVERSA vira SKU de verdade aqui — cria o cadastro certo,
                cancela a venda e relança. Cancelada = auditada. */}
            <section className="cartao p-4">
              <h2 className="rotulo">Peça diversa (9999) — auditar</h2>
              {(() => {
                const pendentes = (coringa.data ?? []).filter((v) => !v.auditada);
                if (pendentes.length === 0) {
                  return <p className="mt-2 text-sm text-dim">Nenhuma venda de coringa pendente de auditoria.</p>;
                }
                return (
                  <ul className="mt-1 divide-y divide-edge">
                    {pendentes.slice(0, 40).map((v) => (
                      <li key={`${v.sale_id}-${v.vendida_em}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">{v.observacao ?? '(sem observação)'}</span>
                          <span className="block text-xs text-dim">
                            {dataBR(v.vendida_em)} · {v.vendedor ?? '—'} · {qtd(v.quantidade)} un
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-alerta">{brl(v.valor)}</span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </section>
          </>
        )}

        {/* AS MARGENS VOLTARAM PARA A GERÊNCIA (28/09/2026, segunda leitura).
            Elas tinham ido junto com o faturamento, e não era isso que o dono
            decidiu: margem de catálogo é CUSTO, não venda — a RPC `margens`
            segue com exige_papel(['gerente','dono']) desde sempre, e a
            migração 090 escreveu, com todas as letras, que custo de fornecedor
            e margem de catálogo para o gerente são "outro assunto, e o dono
            não decidiu sobre ele". Esconder aqui o que o banco entrega seria
            decidir no lugar dele. A régua é a da ROTA (podeGerenciarCatalogo),
            que é quem compra e quem põe preço. */}
        {podeGerenciarCatalogo(papel) && (
          <section className="cartao p-4">
            <h2 className="rotulo">Margens mais apertadas</h2>
            <p className="mb-2 text-xs text-dim">
              Calculada pela RPC <span className="font-mono">margens</span> a partir de custo e preço de
              balcão. Peça sem custo cadastrado não aparece.
            </p>
            {margem.data && margem.data.length > 0 ? (
              <ul className="divide-y divide-edge">
                {margem.data.filter((m) => m.margem_balcao_pct !== null).slice(0, 15).map((m) => (
                  <li key={m.product_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <Link to={`/peca/${m.product_id}`} className="min-w-0 hover:text-signal">
                      <span className="block truncate font-semibold">{m.descricao}</span>
                      <span className="font-mono text-xs text-dim">{m.sku}</span>
                    </Link>
                    <span className={[
                      'shrink-0 font-mono font-bold',
                      Number(m.margem_balcao_pct) < 15 ? 'text-erro'
                        : Number(m.margem_balcao_pct) < 30 ? 'text-alerta' : 'text-ok',
                    ].join(' ')}>
                      {Number(m.margem_balcao_pct).toFixed(1)}%
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-dim">Sem dados de margem — falta preço de custo nas peças.</p>
            )}
          </section>
        )}

        {cadastro.data && (
          <section className="cartao p-4">
            <h2 className="rotulo">Qualidade do cadastro</h2>
            <p className="mt-1 text-sm text-mute">
              <strong className="text-paper">{cadastro.data.completos}</strong> de{' '}
              <strong className="text-paper">{cadastro.data.total}</strong> peças com cadastro completo
              {cadastro.data.total > 0 && (
                <span className="text-dim">
                  {' '}({Math.round((cadastro.data.completos / cadastro.data.total) * 100)}%)
                </span>
              )}
            </p>
            <div className="mt-2 h-2 w-full bg-edge">
              <div
                className="h-2 bg-mute"
                style={{
                  width: `${cadastro.data.total ? (cadastro.data.completos / cadastro.data.total) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="mt-2 text-xs text-dim">
              Meta do plano: 500 SKUs de maior giro em 30 dias (WEB_PLAN §2.6).
            </p>
          </section>
        )}
      </div>
    </Layout>
  );
}

function agrupar<T>(
  linhas: T[], chave: (x: T) => string, valor: (x: T) => number,
): Array<[string, number]> {
  const mapa = new Map<string, number>();
  for (const l of linhas) mapa.set(chave(l), (mapa.get(chave(l)) ?? 0) + valor(l));
  return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * "O DIA NA LOJA" — a folha do fim do expediente.
 *
 * Pedido do dono: um papel bonito e simples para o pai dele, que não usa o
 * sistema, saber o que a loja fez no dia. Fica no TOPO de Relatórios porque é
 * a coisa que se faz todo dia às seis da tarde; os gráficos de 7/30/90 dias
 * são a pergunta de outra hora.
 */
function FolhaDoDia() {
  const [dia, setDia] = useState(hojeISO());
  const [naFila, setNaFila] = useState(false);
  const [filaErro, setFilaErro] = useState<string | null>(null);

  const resumo = useQuery({
    queryKey: ['resumo-do-dia', dia],
    queryFn: () => resumoDoDia(dia),
  });

  const imprimirNaLoja = useMutation({
    mutationFn: async () => {
      if (!resumo.data) throw new Error('O resumo ainda está carregando.');
      setFilaErro(null);
      await enfileirarImpressao({
        tipo: 'relatorio',
        titulo: `O dia na loja — ${diaPorExtenso(dia)}`,
        documentoHtml: montarDocumentoDoDia(resumo.data),
      });
    },
    onSuccess: () => setNaFila(true),
    onError: (e) => setFilaErro(mensagemDeErro(e)),
  });

  const r = resumo.data;

  return (
    <section className="cartao space-y-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-bold">O dia na loja</h2>
        <span className="text-xs text-dim">
          uma folha só, para ler sem o sistema na frente
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
        <div>
          <label className="rotulo" htmlFor="dia-folha">Dia</label>
          <input id="dia-folha" type="date" className="campo" value={dia} max={hojeISO()}
                 onChange={(e) => { setDia(e.target.value); setNaFila(false); }} />
        </div>
        {/* A prévia em três números: quem vai imprimir confere ANTES de gastar
            papel — e vê na hora se pegou o dia errado. */}
        {r && (
          <div className="grid grid-cols-3 gap-2 self-end">
            <div>
              <p className="rotulo">Vendemos</p>
              <p className="font-mono text-base font-black tabular-nums">{brl(Number(r.vendas.total))}</p>
            </div>
            <div>
              <p className="rotulo">Entrou</p>
              <p className="font-mono text-base font-black tabular-nums text-ok">{brl(Number(r.caixa.entrou))}</p>
            </div>
            <div>
              <p className="rotulo">Saiu</p>
              <p className="font-mono text-base font-black tabular-nums text-erro">{brl(Number(r.caixa.saiu))}</p>
            </div>
          </div>
        )}
      </div>

      {resumo.error ? <Aviso tipo="erro">{mensagemDeErro(resumo.error)}</Aviso> : null}
      {filaErro ? (
        <Aviso tipo="alerta">
          A impressora da loja não aceitou agora ({filaErro}). Use "Imprimir aqui".
        </Aviso>
      ) : null}
      {naFila && !filaErro ? (
        <Aviso tipo="ok">Mandado para a impressora da loja.</Aviso>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-primario px-4 py-2 text-sm"
                disabled={!r || imprimirNaLoja.isPending}
                onClick={() => imprimirNaLoja.mutate()}>
          {imprimirNaLoja.isPending ? 'Mandando…' : 'Imprimir na loja'}
        </button>
        <button type="button" className="botao-secundario px-4 py-2 text-sm"
                disabled={!r}
                onClick={() => r && imprimirDia(r)}>
          Imprimir aqui
        </button>
      </div>
    </section>
  );
}

function Barras({
  titulo, itens, formatoRotulo,
}: { titulo: string; itens: Array<[string, number]>; formatoRotulo: (v: string) => string }) {
  if (itens.length === 0) return null;
  const maior = Math.max(...itens.map(([, v]) => v), 1);
  return (
    <section className="cartao p-4">
      <h2 className="rotulo">{titulo}</h2>
      <ul className="mt-2 space-y-1">
        {itens.map(([chave, valor]) => (
          <li key={chave} className="flex items-center gap-3 text-xs">
            <span className="w-16 shrink-0 font-mono text-dim">{formatoRotulo(chave)}</span>
            <span className="h-3.5 bg-mute" style={{ width: `${(valor / maior) * 65}%` }} />
            <span className="font-mono text-mute">{brl(valor)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
